import React, { useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { isPushSupported, getPushPermission } from "../lib/push";

/**
 * NotificationBell
 * Small bell icon rendered in the site Header. Always visible.
 *  - Shows a red dot if the user has not yet activated push
 *  - Clicking dispatches "open-push-optin" so PushOptInPrompt shows on demand
 *  - Silently hidden if the browser does not support push
 */
export const NotificationBell = ({ compact = false }) => {
  const [subscribed, setSubscribed] = useState(true); // optimistic hide
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    if (!isPushSupported()) { setSupported(false); return; }
    if (getPushPermission() === "denied") { setSubscribed(true); return; }
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch {
        setSubscribed(false);
      }
    })();
    const onChange = () => {
      // re-check after a sub/unsub event
      navigator.serviceWorker.ready
        .then((reg) => reg.pushManager.getSubscription())
        .then((s) => setSubscribed(!!s))
        .catch(() => {});
    };
    window.addEventListener("guzz-push-subscription-change", onChange);
    return () => window.removeEventListener("guzz-push-subscription-change", onChange);
  }, []);

  if (!supported) return null;

  const openPrompt = () => {
    window.dispatchEvent(new CustomEvent("open-push-optin", { detail: { source: "bell" } }));
  };

  return (
    <button
      type="button"
      onClick={openPrompt}
      aria-label={subscribed ? "Notifiche attive" : "Attiva notifiche"}
      title={subscribed ? "Notifiche attive" : "Attiva notifiche per le nuove auto"}
      className={`relative inline-flex items-center ${compact ? "p-2" : "p-2 hover:text-gold transition-colors"}`}
      data-testid="header-notification-bell"
    >
      <Bell
        size={compact ? 20 : 18}
        className={subscribed ? "text-gold" : "text-white/70 hover:text-gold"}
        strokeWidth={2}
        fill={subscribed ? "#D4AF37" : "none"}
      />
      {!subscribed && (
        <span
          className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full ring-2 ring-midnight animate-pulse"
          aria-hidden="true"
          data-testid="header-notification-dot"
        />
      )}
    </button>
  );
};

export default NotificationBell;
